import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { PageHeader } from "../components/PortalShell";
import { MessageCircle, Send, Search, ArrowLeft, Filter, X } from "lucide-react";
import { useSupplier } from "../context/SupplierContext";

// Slack-style 2-pane messaging UI shared by both buyer and seller portals.
// The `role` prop changes the title/subtitle wording; all routing + business
// logic is identical since /api/chat/* is symmetric by design.

// Threads list still polls (covers new threads being created); active
// thread upgrades to SSE for instant message delivery.
const THREADS_POLL_MS = 15000;

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

function Avatar({ name }) {
  const initials = (name || "?").split(/\s+/).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
  return (
    <div className="w-9 h-9 rounded-full bg-foreground text-background grid place-items-center text-[12px] font-medium shrink-0">
      {initials}
    </div>
  );
}

export default function MessagesApp({ role = "seller" }) {
  const nav = useNavigate();
  const { search } = useLocation();
  const initialTid = new URLSearchParams(search).get("thread") || null;
  const counterpartParam = new URLSearchParams(search).get("counterpart") || null;

  // Iter 169 · Buyer-side context awareness — when the buyer is inside a
  // specific supplier's storefront, scope the inbox to that supplier only.
  // Hub mode (no active supplier) shows ALL conversations cross-supplier.
  const supplierCtx = useSupplier();
  const activeSupplier = role === "buyer" ? supplierCtx?.active : null;
  const scopedSellerId = activeSupplier?.tenant_owner_user_id || null;

  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(initialTid);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [filter, setFilter] = useState("");
  const [me, setMe] = useState(null);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Iter 169 · If the buyer switches into a supplier storefront while
  // they were viewing a thread with a *different* supplier, deselect so
  // the right pane doesn't show a thread that's no longer in their list.
  useEffect(() => {
    if (!scopedSellerId || !activeId) return;
    const t = threads.find((x) => x.id === activeId);
    if (t && t.counterpart?.id && t.counterpart.id !== scopedSellerId) {
      setActiveId(null);
      nav("", { replace: true });
    }
  }, [scopedSellerId, activeId, threads, nav]);

  const active = useMemo(() => threads.find((t) => t.id === activeId), [threads, activeId]);

  // ── Fetchers ─────────────────────────────────────────────────────────
  const loadThreads = async () => {
    try {
      const { data } = await api.get("/chat/threads");
      setThreads(data);
      if (!activeId && data.length) setActiveId(data[0].id);
    } catch (e) { /* silent on poll */ }
  };
  const loadMessages = async (tid) => {
    if (!tid) return;
    try {
      const { data } = await api.get(`/chat/threads/${tid}/messages`);
      setMessages(data);
      // Auto-mark-read on the server.
      await api.post(`/chat/threads/${tid}/read`).catch(() => {});
    } catch (e) { /* silent */ }
  };

  // ── Mount: identify self + first load ────────────────────────────────
  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
    loadThreads();
  }, []);

  // ── If a counterpart=<userId> is present in the URL, auto-open (or
  // create) the thread with that user. Used by the row-level "Message"
  // action on /seller/buyers and /buyer/suppliers — one click goes
  // directly into a focused conversation. ────────────────────────────
  useEffect(() => {
    if (!counterpartParam) return;
    api.post("/chat/threads", { counterpart_id: counterpartParam })
      .then((r) => {
        setActiveId(r.data.id);
        // Reload thread list so the new thread appears in the sidebar.
        loadThreads();
        // Strip query params so the URL stays clean and re-renders are stable.
        nav("?thread=" + r.data.id, { replace: true });
      })
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Could not open conversation");
      });
  }, [counterpartParam]);

  // ── Load messages whenever the active thread changes ─────────────────
  useEffect(() => {
    if (!activeId) { setMessages([]); return; }
    loadMessages(activeId);
  }, [activeId]);

  // ── Threads list polling (lighter — covers new-thread creation) ──────
  useEffect(() => {
    const id = setInterval(() => { loadThreads(); }, THREADS_POLL_MS);
    return () => clearInterval(id);
  }, []);

  // ── Realtime stream (SSE) for the active thread ──────────────────────
  // Replaces the old 8s poll with a single EventSource per open thread.
  // EventSource auto-reconnects on network blips. Fallback: if the stream
  // fails, we re-fetch on send + threads-list polling still catches drift.
  useEffect(() => {
    if (!activeId) return undefined;
    const token = localStorage.getItem("token");
    if (!token) return undefined;
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const url = `${apiBase}/api/chat/threads/${activeId}/stream?auth=${encodeURIComponent(token)}`;
    let es;
    try { es = new EventSource(url); }
    catch { return undefined; }

    const onMessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (!msg || !msg.id) return;
        setMessages((arr) => (arr.some((x) => x.id === msg.id) ? arr : [...arr, msg]));
        // Auto-mark-read for incoming messages (mine were already read).
        api.post(`/chat/threads/${activeId}/read`).catch(() => {});
      } catch { /* ignore malformed */ }
    };
    es.addEventListener("message", onMessage);
    // EventSource swallows its own "error" but switches to reconnecting state;
    // we don't surface a toast — silent recovery is the right UX here.

    return () => {
      try { es.removeEventListener("message", onMessage); } catch { /* noop */ }
      try { es.close(); } catch { /* noop */ }
    };
  }, [activeId]);

  // ── Autoscroll to the bottom on new messages ─────────────────────────
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, activeId]);

  const send = async (e) => {
    e?.preventDefault?.();
    const body = draft.trim();
    if (!body || !activeId || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/chat/threads/${activeId}/messages`, { body });
      setMessages((m) => [...m, data]);
      setDraft("");
      // Update the thread preview locally for instant feedback.
      setThreads((ts) =>
        ts.map((t) =>
          t.id === activeId
            ? { ...t, last_message_at: data.sent_at, last_message_preview: data.body, last_message_by: data.author_user_id, unread: 0 }
            : t,
        ),
      );
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Could not send message");
    } finally { setSending(false); }
  };

  // Iter 169 · Inside a supplier storefront, scope to that one seller only.
  // In Hub mode, show every thread (cross-supplier).
  const scopedThreads = scopedSellerId
    ? threads.filter((t) => t.counterpart?.id === scopedSellerId)
    : threads;

  const filtered = scopedThreads.filter(
    (t) =>
      !filter ||
      (t.counterpart?.name || "").toLowerCase().includes(filter.toLowerCase()) ||
      (t.last_message_preview || "").toLowerCase().includes(filter.toLowerCase()),
  );

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <>
      <PageHeader
        title="Messages"
        subtitle={
          role === "seller"
            ? "Talk to your buyers — questions, clarifications, updates"
            : scopedSellerId
              ? `Conversations with ${activeSupplier?.tenant_name || "this supplier"}`
              : "All your supplier conversations in one place"
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] h-[calc(100vh-220px)] min-h-[520px] rounded-2xl border border-border overflow-hidden bg-white" data-testid="messages-app">
        {/* ── LEFT: thread list ─────────────────────────────────────── */}
        <aside className={`border-r border-border flex flex-col ${activeId ? "hidden md:flex" : "flex"}`}>
          {/* Iter 169 · Scope chip — visible only when buyer is inside a
              specific supplier's storefront. One-click "Show all" jumps the
              user out to the Verqo hub to see every conversation. */}
          {scopedSellerId && (
            <div className="px-3 pt-3 pb-2 border-b border-border bg-amber-50/60" data-testid="messages-scope-chip">
              <div className="flex items-center justify-between gap-2 text-[11.5px]">
                <span className="inline-flex items-center gap-1.5 text-amber-900">
                  <Filter className="w-3 h-3"/>
                  <span>Scoped to <strong>{activeSupplier?.tenant_name || "this supplier"}</strong></span>
                </span>
                <button
                  type="button"
                  onClick={() => supplierCtx.clearSupplier()}
                  className="inline-flex items-center gap-0.5 text-foreground/60 hover:text-foreground underline underline-offset-2 mono uppercase tracking-wider text-[10px]"
                  data-testid="messages-show-all"
                  title="Show conversations with every supplier"
                >
                  Show all <X className="w-2.5 h-2.5"/>
                </button>
              </div>
            </div>
          )}
          <div className="p-3 border-b border-border">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" />
              <Input
                placeholder="Search conversations"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="pl-8 h-9 rounded-full bg-secondary border-0 text-[13px]"
                data-testid="messages-search"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 && (
              <div className="p-6 text-center text-[13px] text-foreground/50">
                <MessageCircle className="w-6 h-6 mx-auto mb-2 opacity-40" />
                {role === "seller"
                  ? "No conversations yet. Buyers will message you here."
                  : scopedSellerId
                    ? `No conversations with ${activeSupplier?.tenant_name || "this supplier"} yet.`
                    : "No conversations yet. Open a supplier's page to start a chat."}
              </div>
            )}
            {filtered.map((t) => {
              const isActive = t.id === activeId;
              const unread = t.unread || 0;
              return (
                <button
                  key={t.id}
                  onClick={() => { setActiveId(t.id); nav(`?thread=${t.id}`, { replace: true }); }}
                  className={`w-full text-left px-4 py-3 flex gap-3 border-b border-border/60 transition-colors ${isActive ? "bg-secondary" : "hover:bg-secondary/50"}`}
                  data-testid={`thread-row-${t.id}`}
                >
                  <Avatar name={t.counterpart?.name} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className={`text-[13.5px] truncate ${unread ? "font-semibold" : "font-medium"}`}>{t.counterpart?.name || "Unknown"}</div>
                      <div className="text-[11px] text-foreground/45 shrink-0">{timeAgo(t.last_message_at)}</div>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-0.5">
                      <div className={`text-[12.5px] truncate ${unread ? "text-foreground" : "text-foreground/55"}`}>
                        {t.last_message_preview || <span className="italic">No messages yet</span>}
                      </div>
                      {unread > 0 && (
                        <span className="shrink-0 min-w-[20px] h-[20px] px-1.5 rounded-full bg-accent text-foreground text-[11px] font-semibold grid place-items-center" data-testid={`unread-${t.id}`}>
                          {unread > 99 ? "99+" : unread}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* ── RIGHT: active conversation ────────────────────────────── */}
        <section className={`flex flex-col ${activeId ? "flex" : "hidden md:flex"}`}>
          {!active && (
            <div className="flex-1 grid place-items-center text-[13px] text-foreground/50">
              <div className="text-center">
                <MessageCircle className="w-8 h-8 mx-auto mb-3 opacity-40" />
                Pick a conversation
              </div>
            </div>
          )}
          {active && (
            <>
              <header className="h-[58px] px-4 border-b border-border flex items-center gap-3">
                <button type="button" className="md:hidden -ml-1 p-1" onClick={() => setActiveId(null)} aria-label="Back">
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <Avatar name={active.counterpart?.name} />
                <div className="min-w-0">
                  <div className="text-[14px] font-semibold truncate" data-testid="active-thread-name">{active.counterpart?.name}</div>
                  <div className="text-[11px] text-foreground/50 uppercase tracking-wide">{active.counterpart?.role}</div>
                </div>
              </header>

              <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 md:px-6 py-5 space-y-3 bg-[#fafaf7]">
                {messages.length === 0 && (
                  <div className="text-center text-[13px] text-foreground/40 py-10">
                    Say hi — it&apos;s a brand new thread.
                  </div>
                )}
                {messages.map((m) => {
                  const mine = me && m.author_user_id === me.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
                      <div className={`max-w-[78%] rounded-2xl px-3.5 py-2.5 text-[14px] leading-snug ${mine ? "bg-foreground text-background rounded-tr-sm" : "bg-white border border-border rounded-tl-sm"}`}>
                        {!mine && <div className="text-[11px] font-medium text-foreground/55 mb-0.5">{m.author_name}</div>}
                        <div className="whitespace-pre-wrap break-words">{m.body}</div>
                        <div className={`text-[10.5px] mt-1 ${mine ? "text-background/60" : "text-foreground/40"}`}>{timeAgo(m.sent_at)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <form onSubmit={send} className="p-3 border-t border-border bg-white flex gap-2 items-end">
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Type a message…  (Shift+Enter for new line)"
                  rows={1}
                  className="flex-1 resize-none rounded-xl border border-border px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-foreground/10 max-h-[140px]"
                  data-testid="message-composer"
                />
                <Button type="submit" disabled={!draft.trim() || sending} data-testid="message-send" className="rounded-full bg-foreground text-background hover:bg-foreground/90 px-4 h-10">
                  <Send className="w-3.5 h-3.5 mr-1" /> Send
                </Button>
              </form>
            </>
          )}
        </section>
      </div>
    </>
  );
}
